<template>
  <div class="obras-nueva">
    <div class="content-header">
      <h1 class="content-header-title">Nueva Obra</h1>
      <p class="content-header-subtitle">Crear una nueva obra</p>
    </div>
    <b-card>
      <b-form @submit.prevent="guardarObra">
        <b-row>
          <!-- Información básica -->
          <b-col md="6">
            <h5 class="section-title">Información Básica</h5>
            
            <b-form-group label="Dirección" required>
              <b-form-input 
                v-model="obra.direccion" 
                placeholder="Ingrese la dirección de la obra"
                required 
                :state="validaciones.direccion"
                name="direccion"
              />
              <b-form-invalid-feedback :state="validaciones.direccion">
                La dirección es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Referencia Externa">
              <b-form-input 
                v-model="obra.referenciaExterna" 
                placeholder="Referencia externa (opcional)"
              />
            </b-form-group>

            <b-form-group label="Referencia de Obra">
              <b-form-select 
                v-model="obra.idReferenciaObra" 
                :options="referenciasObras"
                value-field="idReferenciaObra"
                text-field="nombre"
                placeholder="Seleccione una referencia"
              />
            </b-form-group>

            <b-form-group label="Fase" required>
              <b-form-select 
                v-model="obra.idFase" 
                :options="fases"
                value-field="idFase"
                text-field="nombre"
                placeholder="Seleccione una fase"
                required
                :state="validaciones.idFase"
                name="idFase"
              />
              <b-form-invalid-feedback :state="validaciones.idFase">
                La fase es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Tipo de Obra" required>
              <b-form-select 
                v-model="obra.idTipoObra" 
                :options="tiposObras"
                value-field="idTipoObra"
                text-field="nombre"
                placeholder="Seleccione un tipo de obra"
                required
                :state="validaciones.idTipoObra"
                name="idTipoObra"
              />
              <b-form-invalid-feedback :state="validaciones.idTipoObra">
                El tipo de obra es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Estado de Obra" required>
              <b-form-select 
                v-model="obra.idEstadoObra" 
                :options="estadosObras"
                value-field="idEstadoObra"
                text-field="nombre"
                placeholder="Seleccione un estado"
                required
                :state="validaciones.idEstadoObra"
                name="idEstadoObra"
              />
              <b-form-invalid-feedback :state="validaciones.idEstadoObra">
                El estado de obra es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Ubicación -->
          <b-col md="6">
            <h5 class="section-title">Ubicación</h5>
            
            <b-form-group label="Municipio" required>
              <b-form-input 
                v-model="obra.municipio" 
                placeholder="Ingrese el municipio"
                required 
                :state="validaciones.municipio"
                name="municipio"
              />
              <b-form-invalid-feedback :state="validaciones.municipio">
                El municipio es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Provincia" required>
              <b-form-input 
                v-model="obra.provincia" 
                placeholder="Ingrese la provincia"
                required 
                :state="validaciones.provincia"
                name="provincia"
              />
              <b-form-invalid-feedback :state="validaciones.provincia">
                La provincia es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Código Postal" required>
              <b-form-input 
                v-model="obra.codigoPostal" 
                type="number"
                placeholder="Ingrese el código postal"
                required 
                :state="validaciones.codigoPostal"
                name="codigoPostal"
              />
              <b-form-invalid-feedback :state="validaciones.codigoPostal">
                El código postal es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Empresa" required>
              <div class="d-flex gap-2">
                <b-form-select 
                  v-model="obra.idEmpresa" 
                  :options="empresas"
                  value-field="idEmpresa"
                  text-field="nombre"
                  placeholder="Seleccione una empresa"
                  required
                  class="flex-grow-1"
                  :state="validaciones.idEmpresa"
                  name="idEmpresa"
                />
                <b-button 
                  variant="outline-primary" 
                  size="sm" 
                  @click="mostrarModalNuevaEmpresa"
                  title="Crear nueva empresa"
                >
                  <feather-icon icon="PlusIcon" size="16" />
                </b-button>
              </div>
              <b-form-invalid-feedback :state="validaciones.idEmpresa">
                La empresa es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Fechas -->
          <b-col md="6">
            <h5 class="section-title">Fechas</h5>
            
            <b-form-group label="Fecha de Inicio" required>
              <b-form-input 
                v-model="obra.fechaInicioObra" 
                type="date"
                required 
                :state="validaciones.fechaInicioObra"
                name="fechaInicioObra"
              />
              <b-form-invalid-feedback :state="validaciones.fechaInicioObra">
                La fecha de inicio es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Fecha de Fin">
              <b-form-input 
                v-model="obra.fechaFinObra" 
                type="date"
              />
            </b-form-group>
          </b-col>

          <!-- Información adicional -->
          <b-col md="6">
            <h5 class="section-title">Información Adicional</h5>
            
            <b-form-group label="Magnitudes">
              <b-form-textarea 
                v-model="obra.magnitudes" 
                placeholder="Descripción de las magnitudes de la obra"
                rows="3"
              />
        </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <h5 class="section-title">Descripción</h5>
            <b-form-group label="Descripción de la Obra">
              <b-form-textarea 
                v-model="obra.descripcion" 
                placeholder="Descripción detallada de la obra"
                rows="4"
              />
        </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-between gap-2 mt-4">
          <b-button variant="outline-info" @click="mostrarModalImportarConstrudata">
            <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
            Importar desde Construdata
          </b-button>
          <div class="d-flex gap-2">
            <b-button variant="outline-secondary" @click="volver">Cancelar</b-button>
            <b-button type="submit" variant="primary" :disabled="guardando">
              {{ guardando ? 'Guardando...' : 'Guardar Obra' }}
            </b-button>
          </div>
        </div>
      </b-form>
    </b-card>

    <!-- Modal para crear nueva empresa -->
    <b-modal
      id="modal-nueva-empresa"
      ref="modalNuevaEmpresa"
      title="Nueva Empresa"
      size="lg"
      @hidden="limpiarFormularioEmpresa"
      @ok="crearEmpresa"
      :ok-disabled="!formularioEmpresaValido || guardandoEmpresa"
    >
      <b-form>
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre de la Empresa" required>
              <b-form-input 
                v-model="nuevaEmpresa.nombre" 
                placeholder="Ingrese el nombre de la empresa"
                required 
                :state="validacionesEmpresa.nombre"
              />
              <b-form-invalid-feedback v-if="!validacionesEmpresa.nombre">
                El nombre es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="CIF" required>
              <b-form-input 
                v-model="nuevaEmpresa.cif" 
                placeholder="Ingrese el CIF de la empresa"
                required 
                :state="validacionesEmpresa.cif"
              />
              <b-form-invalid-feedback v-if="!validacionesEmpresa.cif">
                El CIF es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Tipo de Empresa" required>
              <b-form-select 
                v-model="nuevaEmpresa.idTipoEmpresa" 
                :options="tiposEmpresa"
                value-field="idTipoEmpresa"
                text-field="nombre"
                placeholder="Seleccione un tipo de empresa"
                required
                :state="validacionesEmpresa.tipoEmpresa"
              />
              <b-form-invalid-feedback v-if="!validacionesEmpresa.tipoEmpresa">
                El tipo de empresa es obligatorio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Dirección" required>
              <b-form-textarea 
                v-model="nuevaEmpresa.direccion" 
                placeholder="Ingrese la dirección de la empresa"
                rows="3"
                required 
                :state="validacionesEmpresa.direccion"
              />
              <b-form-invalid-feedback v-if="!validacionesEmpresa.direccion">
                La dirección es obligatoria
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Email">
              <b-form-input 
                v-model="nuevaEmpresa.email" 
                type="email"
                placeholder="email@empresa.com"
                :state="validacionesEmpresa.email"
              />
              <b-form-invalid-feedback v-if="!validacionesEmpresa.email">
                Formato de email inválido
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Teléfono">
              <b-form-input 
                v-model="nuevaEmpresa.telefono" 
                placeholder="+34 123 456 789"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok"
          :disabled="!formularioEmpresaValido || guardandoEmpresa"
        >
          <b-spinner v-if="guardandoEmpresa" small class="mr-1" />
          {{ guardandoEmpresa ? 'Creando...' : 'Crear Empresa' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para importar desde Construdata -->
    <b-modal
      id="modal-importar-construdata"
      ref="modalImportarConstrudata"
      title="Importar desde Construdata"
      size="lg"
      @hidden="limpiarFormularioConstrudata"
      @ok="importarDatosConstrudata"
      :ok-disabled="!textoConstrudata.trim() || procesandoConstrudata"
    >
      <div class="importar-construdata">
        <p class="text-muted mb-3">
          Pega aquí el texto copiado desde Construdata para importar automáticamente los datos de la obra.
        </p>
        
        <b-form-group label="Texto de Construdata" required>
          <b-form-textarea 
            v-model="textoConstrudata" 
            placeholder="Pega aquí el texto de Construdata..."
            rows="12"
            required 
            :state="textoConstrudata.trim() ? true : null"
          />
          <b-form-text class="mt-2">
            <small>Ejemplo de formato esperado:</small><br>
            <code class="text-muted">
              Entrelux III	Ref.: 417-1918<br>
              DIRECCIÓN<br>
              Dos Hermanas (41700) Sevilla, Andalucía, ESPAÑA<br>
              TIPO DE OBRA<br>
              Instalaciones Deportivas / Viviendas Unifamiliares<br>
              ...
            </code>
          </b-form-text>
        </b-form-group>

        <div v-if="datosParseados" class="mt-3">
          <h6 class="text-success mb-2">
            <feather-icon icon="CheckCircleIcon" size="16" class="mr-1" />
            Datos detectados:
          </h6>
          <div class="datos-detectados">
            <div v-if="datosParseados.nombre" class="mb-1">
              <strong>Nombre:</strong> {{ datosParseados.nombre }}
            </div>
            <div v-if="datosParseados.referencia" class="mb-1">
              <strong>Referencia:</strong> {{ datosParseados.referencia }}
            </div>
            <div v-if="datosParseados.direccion" class="mb-1">
              <strong>Dirección:</strong> {{ datosParseados.direccion }}
            </div>
            <div v-if="datosParseados.municipio" class="mb-1">
              <strong>Municipio:</strong> {{ datosParseados.municipio }}
            </div>
            <div v-if="datosParseados.provincia" class="mb-1">
              <strong>Provincia:</strong> {{ datosParseados.provincia }}
            </div>
            <div v-if="datosParseados.codigoPostal" class="mb-1">
              <strong>Código Postal:</strong> {{ datosParseados.codigoPostal }}
            </div>
            <div v-if="datosParseados.tipoObra" class="mb-1">
              <strong>Tipo de Obra:</strong> {{ datosParseados.tipoObra }}
            </div>
            <div v-if="datosParseados.estadoObra" class="mb-1">
              <strong>Estado:</strong> {{ datosParseados.estadoObra }}
            </div>
            <div v-if="datosParseados.presupuesto" class="mb-1">
              <strong>Presupuesto:</strong> {{ datosParseados.presupuesto.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) }}
            </div>
            <div v-if="datosParseados.unidadesHabitacion" class="mb-1">
              <strong>Unidades de Habitación:</strong> {{ datosParseados.unidadesHabitacion }}
            </div>
            <div v-if="datosParseados.promotor" class="mb-1">
              <strong>Promotor:</strong> {{ datosParseados.promotor }}
            </div>
            <div v-if="datosParseados.descripcion" class="mb-1">
              <strong>Descripción:</strong> {{ datosParseados.descripcion.substring(0, 100) }}{{ datosParseados.descripcion.length > 100 ? '...' : '' }}
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok"
          :disabled="!textoConstrudata.trim() || procesandoConstrudata"
        >
          <b-spinner v-if="procesandoConstrudata" small class="mr-1" />
          {{ procesandoConstrudata ? 'Procesando...' : 'Importar Datos' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { ObrasMixin } from '@/@core/mixins/catalogos'
import { EmpresaService } from '@/services/empresaService'
import { TipoEmpresaService } from '@/services/tipoEmpresaService'
import { ObraService } from '@/services/obraService'

export default {
  name: 'ObrasNueva',
  mixins: [ObrasMixin],
  data() {
    return {
      // Datos para el modal de nueva empresa
      nuevaEmpresa: {
        nombre: '',
        cif: '',
        direccion: '',
        idTipoEmpresa: null,
        email: '',
        telefono: ''
      },
      tiposEmpresa: [],
      guardandoEmpresa: false,
      validacionesEmpresa: {
        nombre: null,
        cif: null,
        direccion: null,
        tipoEmpresa: null,
        email: null
      },
      // Datos para importar desde Construdata
      textoConstrudata: '',
      datosParseados: null,
      procesandoConstrudata: false,
      // Validaciones del formulario
      validaciones: {
        direccion: null,
        idFase: null,
        municipio: null,
        codigoPostal: null,
        idEstadoObra: null,
        provincia: null,
        idTipoObra: null,
        fechaInicioObra: null,
        idEmpresa: null
      }
    }
  },
  computed: {
    formularioEmpresaValido() {
      return this.validacionesEmpresa.nombre && 
             this.validacionesEmpresa.cif && 
             this.validacionesEmpresa.direccion && 
             this.validacionesEmpresa.tipoEmpresa &&
             this.validacionesEmpresa.email !== false
    }
  },
  async mounted() {
    await this.cargarCatalogos()
    this.preseleccionarEmpresa()
    await this.cargarTiposEmpresa()
  },
  methods: {
    async cargarTiposEmpresa() {
      try {
        this.tiposEmpresa = await TipoEmpresaService.getAll()
      } catch (error) {
        console.error('Error cargando tipos de empresa:', error)
        this.tiposEmpresa = []
      }
    },
    mostrarModalNuevaEmpresa() {
      this.$refs.modalNuevaEmpresa.show()
    },
    limpiarFormularioEmpresa() {
      this.nuevaEmpresa = {
        nombre: '',
        cif: '',
        direccion: '',
        idTipoEmpresa: null,
        email: '',
        telefono: ''
      }
      this.validacionesEmpresa = {
        nombre: null,
        cif: null,
        direccion: null,
        tipoEmpresa: null,
        email: null
      }
    },
    validarFormularioEmpresa() {
      // Validar nombre
      this.validacionesEmpresa.nombre = this.nuevaEmpresa.nombre && this.nuevaEmpresa.nombre.trim().length > 0
      
      // Validar CIF
      this.validacionesEmpresa.cif = this.nuevaEmpresa.cif && this.nuevaEmpresa.cif.trim().length > 0
      
      // Validar dirección
      this.validacionesEmpresa.direccion = this.nuevaEmpresa.direccion && this.nuevaEmpresa.direccion.trim().length > 0
      
      // Validar tipo de empresa
      this.validacionesEmpresa.tipoEmpresa = this.nuevaEmpresa.idTipoEmpresa !== null
      
      // Validar email (opcional pero si se proporciona debe ser válido)
      if (this.nuevaEmpresa.email && this.nuevaEmpresa.email.trim()) {
        this.validacionesEmpresa.email = this.validarEmailFormato(this.nuevaEmpresa.email)
      } else {
        this.validacionesEmpresa.email = null
      }
    },
    validarEmailFormato(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    async crearEmpresa() {
      this.validarFormularioEmpresa()
      
      if (!this.formularioEmpresaValido) {
        this.$toast.error('Por favor, corrige los errores en el formulario')
        return
      }

      this.guardandoEmpresa = true
      
      try {
        // Preparar datos para envío
        const datosEmpresa = {
          ...this.nuevaEmpresa,
          web: undefined,
          observaciones: undefined
        }

        const empresaCreada = await EmpresaService.create(datosEmpresa)
        
        // Agregar la nueva empresa a la lista
        this.empresas.push(empresaCreada)
        this.empresas.sort((a, b) => a.nombre.localeCompare(b.nombre))
        
        // Preseleccionar la empresa recién creada
        this.obra.idEmpresa = empresaCreada.idEmpresa
        
        this.$toast.success('Empresa creada correctamente')
        this.$refs.modalNuevaEmpresa.hide()
        this.limpiarFormularioEmpresa()
      } catch (error) {
        this.$toast.error('Error al crear la empresa')
        console.error('Error:', error)
      } finally {
        this.guardandoEmpresa = false
      }
    },
    // Métodos para importar desde Construdata
    mostrarModalImportarConstrudata() {
      this.$refs.modalImportarConstrudata.show()
    },
    limpiarFormularioConstrudata() {
      this.textoConstrudata = ''
      this.datosParseados = null
      this.procesandoConstrudata = false
    },
    async importarDatosConstrudata() {
      if (!this.textoConstrudata.trim()) {
        this.$toast.error('Por favor, pega el texto de Construdata')
        return
      }

      this.procesandoConstrudata = true
      
      try {
        // Parsear el texto de Construdata
        const datos = this.parsearTextoConstrudata(this.textoConstrudata)
        this.datosParseados = datos
        
        // Mapear los datos a los campos del formulario
        this.mapearDatosAFormulario(datos)
        
        this.$toast.success('Datos importados correctamente')
        this.$refs.modalImportarConstrudata.hide()
        this.limpiarFormularioConstrudata()
      } catch (error) {
        this.$toast.error('Error al procesar los datos de Construdata')
        console.error('Error:', error)
      } finally {
        this.procesandoConstrudata = false
      }
    },
    parsearTextoConstrudata(texto) {
      const lineas = texto.split('\n').map(linea => linea.trim()).filter(linea => linea)
      const datos = {}
      
      // Buscar nombre y referencia en la primera línea
      const primeraLinea = lineas[0] || ''
      const matchRef = primeraLinea.match(/Ref\.:\s*([^\s]+)/)
      if (matchRef) {
        datos.referencia = matchRef[1]
        datos.nombre = primeraLinea.replace(/\s*Ref\.:\s*[^\s]+.*/, '').trim()
      } else {
        datos.nombre = primeraLinea
      }
      
      // Buscar dirección
      const indiceDireccion = lineas.findIndex(linea => 
        linea.toUpperCase().includes('DIRECCIÓN') || 
        linea.toUpperCase().includes('DIRECCION')
      )
      if (indiceDireccion !== -1 && indiceDireccion + 1 < lineas.length) {
        const direccionCompleta = lineas[indiceDireccion + 1]
        datos.direccion = direccionCompleta
        
        // Extraer municipio, provincia y código postal
        const matchDireccion = direccionCompleta.match(/^([^(]+)\s*\((\d{5})\)\s*(.+),\s*(.+)$/)
        if (matchDireccion) {
          datos.municipio = matchDireccion[1].trim()
          datos.codigoPostal = matchDireccion[2]
          datos.provincia = matchDireccion[3].trim()
        }
      }
      
      // Buscar tipo de obra
      const indiceTipoObra = lineas.findIndex(linea => 
        linea.toUpperCase().includes('TIPO DE OBRA')
      )
      if (indiceTipoObra !== -1 && indiceTipoObra + 1 < lineas.length) {
        datos.tipoObra = lineas[indiceTipoObra + 1]
      }
      
      // Buscar estado de obra
      const indiceEstadoObra = lineas.findIndex(linea => 
        linea.toUpperCase().includes('ESTADO DE OBRA')
      )
      if (indiceEstadoObra !== -1 && indiceEstadoObra + 1 < lineas.length) {
        datos.estadoObra = lineas[indiceEstadoObra + 1]
      }
      
      // Buscar presupuesto
      const indicePresupuesto = lineas.findIndex(linea => 
        linea.toUpperCase().includes('PRESUPUESTO')
      )
      if (indicePresupuesto !== -1 && indicePresupuesto + 1 < lineas.length) {
        const presupuestoTexto = lineas[indicePresupuesto + 1]
        // Extraer solo números y comas/puntos decimales
        const matchPresupuesto = presupuestoTexto.match(/[\d.,]+/)
        if (matchPresupuesto) {
          // Convertir a número, reemplazando comas por puntos si es necesario
          const numero = matchPresupuesto[0].replace(',', '.')
          datos.presupuesto = parseFloat(numero)
        }
      }
      
      // Buscar unidades de habitación
      const indiceUnidades = lineas.findIndex(linea => 
        linea.toUpperCase().includes('UDS. DE HABITACIÓN') ||
        linea.toUpperCase().includes('UNIDADES DE HABITACIÓN') ||
        linea.toUpperCase().includes('UDS DE HABITACION') ||
        linea.toUpperCase().includes('UNIDADES DE HABITACION')
      )
      if (indiceUnidades !== -1 && indiceUnidades + 1 < lineas.length) {
        const unidadesTexto = lineas[indiceUnidades + 1]
        const matchUnidades = unidadesTexto.match(/\d+/)
        if (matchUnidades) {
          datos.unidadesHabitacion = parseInt(matchUnidades[0])
        }
      }
      
      // Buscar promotores
      const indicePromotores = lineas.findIndex(linea => 
        linea.toUpperCase().includes('PROMOTORES')
      )
      if (indicePromotores !== -1 && indicePromotores + 1 < lineas.length) {
        datos.promotor = lineas[indicePromotores + 1]
      }
      
      // Buscar descripción
      const indiceDescripcion = lineas.findIndex(linea => 
        linea.toUpperCase().includes('DESCRIPCIÓN') || 
        linea.toUpperCase().includes('DESCRIPCION')
      )
      if (indiceDescripcion !== -1 && indiceDescripcion + 1 < lineas.length) {
        // La descripción puede ocupar varias líneas
        let descripcion = ''
        for (let i = indiceDescripcion + 1; i < lineas.length; i++) {
          if (lineas[i].toUpperCase().includes('PROMOTORES')) {
            break
          }
          if (descripcion) descripcion += ' '
          descripcion += lineas[i]
        }
        datos.descripcion = descripcion.trim()
      }
      
      return datos
    },
    mapearDatosAFormulario(datos) {
      // Mapear datos básicos
      if (datos.nombre) {
        // Si no hay un campo específico para nombre, podríamos usar la descripción
        // o crear un campo adicional según la estructura de la base de datos
      }
      
      if (datos.referencia) {
        this.obra.referenciaExterna = datos.referencia
      }
      
      if (datos.direccion) {
        this.obra.direccion = datos.direccion
      }
      
      if (datos.municipio) {
        this.obra.municipio = datos.municipio
      }
      
      if (datos.provincia) {
        this.obra.provincia = datos.provincia
      }
      
      if (datos.codigoPostal) {
        this.obra.codigoPostal = datos.codigoPostal
      }
      
      if (datos.descripcion) {
        this.obra.descripcion = datos.descripcion
      }
      
      // Mapear tipo de obra (buscar coincidencia en las opciones disponibles)
      if (datos.tipoObra) {
        const tipoEncontrado = this.tiposObras.find(tipo => 
          tipo.nombre.toLowerCase().includes(datos.tipoObra.toLowerCase()) ||
          datos.tipoObra.toLowerCase().includes(tipo.nombre.toLowerCase())
        )
        if (tipoEncontrado) {
          this.obra.idTipoObra = tipoEncontrado.idTipoObra
        }
      }
      
      // Mapear estado de obra
      if (datos.estadoObra) {
        const estadoEncontrado = this.estadosObras.find(estado => 
          estado.nombre.toLowerCase().includes(datos.estadoObra.toLowerCase()) ||
          datos.estadoObra.toLowerCase().includes(estado.nombre.toLowerCase())
        )
        if (estadoEncontrado) {
          this.obra.idEstadoObra = estadoEncontrado.idEstadoObra
        }
      }
      
      // Si hay un promotor, podríamos crear una empresa automáticamente o sugerir crear una
      if (datos.promotor) {
        // Por ahora solo mostramos un mensaje informativo
        this.$toast.info(`Promotor detectado: ${datos.promotor}. Puedes crear una empresa para este promotor.`)
      }
      
      // Mostrar información sobre campos adicionales que no se pueden mapear directamente
      if (datos.presupuesto || datos.unidadesHabitacion) {
        let mensaje = 'Información adicional detectada: '
        if (datos.presupuesto) {
          mensaje += `Presupuesto: ${datos.presupuesto.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}. `
        }
        if (datos.unidadesHabitacion) {
          mensaje += `Unidades de habitación: ${datos.unidadesHabitacion}. `
        }
        mensaje += 'Estos datos se mostrarán en la vista previa pero no se guardarán automáticamente.'
        this.$toast.info(mensaje)
      }
    },
    validarFormulario() {
      let esValido = true
      let primerCampoError = null
      
      // Validar dirección
      this.validaciones.direccion = !!(this.obra.direccion && this.obra.direccion.trim().length > 0)
      if (!this.validaciones.direccion) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'direccion'
      }
      
      // Validar fase
      this.validaciones.idFase = this.obra.idFase !== null && this.obra.idFase !== undefined
      if (!this.validaciones.idFase) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'idFase'
      }
      
      // Validar municipio
      this.validaciones.municipio = !!(this.obra.municipio && this.obra.municipio.trim().length > 0)
      if (!this.validaciones.municipio) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'municipio'
      }
      
      // Validar código postal
      this.validaciones.codigoPostal = !!(this.obra.codigoPostal && this.obra.codigoPostal.toString().trim().length > 0)
      if (!this.validaciones.codigoPostal) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'codigoPostal'
      }
      
      // Validar estado de obra
      this.validaciones.idEstadoObra = this.obra.idEstadoObra !== null && this.obra.idEstadoObra !== undefined
      if (!this.validaciones.idEstadoObra) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'idEstadoObra'
      }
      
      // Validar provincia
      this.validaciones.provincia = !!(this.obra.provincia && this.obra.provincia.trim().length > 0)
      if (!this.validaciones.provincia) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'provincia'
      }
      
      // Validar tipo de obra
      this.validaciones.idTipoObra = this.obra.idTipoObra !== null && this.obra.idTipoObra !== undefined
      if (!this.validaciones.idTipoObra) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'idTipoObra'
      }
      
      // Validar fecha de inicio
      this.validaciones.fechaInicioObra = !!(this.obra.fechaInicioObra && this.obra.fechaInicioObra.trim().length > 0)
      if (!this.validaciones.fechaInicioObra) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'fechaInicioObra'
      }
      
      // Validar empresa
      this.validaciones.idEmpresa = this.obra.idEmpresa !== null && this.obra.idEmpresa !== undefined
      if (!this.validaciones.idEmpresa) {
        esValido = false
        if (!primerCampoError) primerCampoError = 'idEmpresa'
      }
      
      // Validar que la fecha de fin sea posterior a la de inicio (solo si ambas fechas están presentes)
      if (this.obra.fechaFinObra && this.obra.fechaInicioObra) {
        if (new Date(this.obra.fechaInicioObra) >= new Date(this.obra.fechaFinObra)) {
          this.$toast.warning('La fecha de fin debe ser posterior a la fecha de inicio')
          esValido = false
        }
      }
      
      if (!esValido) {
        this.$toast.warning('Por favor, completa todos los campos requeridos')
        // Hacer scroll al primer campo con error
        this.$nextTick(() => {
          const campoError = document.querySelector(`[name="${primerCampoError}"]`)
          if (campoError) {
            campoError.scrollIntoView({ behavior: 'smooth', block: 'center' })
            campoError.focus()
          }
        })
      }
      
      return esValido
    },
    async guardarObra() {
      if (!this.validarFormulario()) {
        return
      }
      
      this.guardando = true
      try {
        // Preparar los datos para enviar al backend
        const obraData = {
          idObra: null,
          direccion: this.obra.direccion,
          referenciaExterna: this.obra.referenciaExterna || undefined,
          idReferenciaObra: this.obra.idReferenciaObra,
          idFase: this.obra.idFase,
          municipio: this.obra.municipio,
          codigoPostal: this.obra.codigoPostal,
          descripcion: this.obra.descripcion || undefined,
          idEstadoObra: this.obra.idEstadoObra,
          provincia: this.obra.provincia,
          idTipoObra: this.obra.idTipoObra,
          fechaInicioObra: this.obra.fechaInicioObra,
          fechaFinObra: this.obra.fechaFinObra || undefined,
          magnitudes: this.obra.magnitudes || undefined,
          idEmpresa: this.obra.idEmpresa,
          ownerId: undefined
        }
        
        await ObraService.create(obraData)
        
        this.$toast.success('Obra creada correctamente')
        this.$router.push({ name: 'obras-lista' })
      } catch (error) {
        console.error('Error guardando obra:', error)
        if (error.response && error.response.status === 400) {
          this.$toast.warning('Datos inválidos. Verifique la información ingresada.')
        } else {
          this.$toast.error('Error al guardar la obra')
        }
      } finally {
        this.guardando = false
      }
    }
  },
  watch: {
    nuevaEmpresa: {
      handler() {
        this.validarFormularioEmpresa()
      },
      deep: true
    },
    textoConstrudata: {
      handler(nuevoTexto) {
        if (nuevoTexto && nuevoTexto.trim()) {
          try {
            this.datosParseados = this.parsearTextoConstrudata(nuevoTexto)
          } catch (error) {
            console.error('Error parseando texto de Construdata:', error)
            this.datosParseados = null
          }
        } else {
          this.datosParseados = null
        }
      }
    },
    'obra.direccion'() {
      if (this.validaciones.direccion === false && this.obra.direccion && this.obra.direccion.trim().length > 0) {
        this.validaciones.direccion = true
      }
    },
    'obra.idFase'() {
      if (this.validaciones.idFase === false && this.obra.idFase !== null && this.obra.idFase !== undefined) {
        this.validaciones.idFase = true
      }
    },
    'obra.municipio'() {
      if (this.validaciones.municipio === false && this.obra.municipio && this.obra.municipio.trim().length > 0) {
        this.validaciones.municipio = true
      }
    },
    'obra.codigoPostal'() {
      if (this.validaciones.codigoPostal === false && this.obra.codigoPostal && this.obra.codigoPostal.trim().length > 0) {
        this.validaciones.codigoPostal = true
      }
    },
    'obra.idEstadoObra'() {
      if (this.validaciones.idEstadoObra === false && this.obra.idEstadoObra !== null && this.obra.idEstadoObra !== undefined) {
        this.validaciones.idEstadoObra = true
      }
    },
    'obra.provincia'() {
      if (this.validaciones.provincia === false && this.obra.provincia && this.obra.provincia.trim().length > 0) {
        this.validaciones.provincia = true
      }
    },
    'obra.idTipoObra'() {
      if (this.validaciones.idTipoObra === false && this.obra.idTipoObra !== null && this.obra.idTipoObra !== undefined) {
        this.validaciones.idTipoObra = true
      }
    },
    'obra.fechaInicioObra'() {
      if (this.validaciones.fechaInicioObra === false && this.obra.fechaInicioObra && this.obra.fechaInicioObra.trim().length > 0) {
        this.validaciones.fechaInicioObra = true
      }
    },
    'obra.idEmpresa'() {
      if (this.validaciones.idEmpresa === false && this.obra.idEmpresa !== null && this.obra.idEmpresa !== undefined) {
        this.validaciones.idEmpresa = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.obras-nueva {
  padding: 1.5rem;
}

.content-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.gap-2 {
  gap: 0.5rem;
}

// Mejorar la apariencia de los campos
.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-control {
  border-radius: 0.375rem;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.form-select {
  border-radius: 0.375rem;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  
  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

// Estilos para los botones
.btn {
  border-radius: 0.375rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  
  &:hover {
    background-color: #0056b3;
    border-color: #0056b3;
  }
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
  
  &:hover {
    background-color: #6c757d;
    border-color: #6c757d;
  }
}

// Espaciado mejorado
.mt-4 {
  margin-top: 1.5rem !important;
}

// Estilos para el campo de empresa con botón
.d-flex.gap-2 {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.flex-grow-1 {
  flex-grow: 1;
}

// Estilos para el modal de importación de Construdata
.importar-construdata {
  .datos-detectados {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 0.375rem;
    padding: 1rem;
    
    div {
      font-size: 0.9rem;
      line-height: 1.4;
    }
    
    strong {
      color: #495057;
      font-weight: 600;
    }
  }
  
  code {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
    color: #6c757d;
    display: block;
    white-space: pre-line;
    margin-top: 0.5rem;
  }
}

// Responsive
@media (max-width: 768px) {
  .obras-nueva {
    padding: 1rem;
  }
  
  .content-header-title {
    font-size: 1.5rem;
  }
  
  .d-flex.gap-2 {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
